import React from "react";
import Assistant from "./Assistant";
import TableRow from "./TableRow";
import TableForAssistant from "./TableForAssistant";
import { PARAMETER_OPTIONS } from "../../../../../../constants";
import TooltipPortal from "../../../../../../parts/tooltip-portal";

const { RPE, RPEAMRAP } = PARAMETER_OPTIONS;

const ExerciseContent = ({
  exercise,
  someHasRPE,
  finalSets,
  newExercise,
  editDayMode,
  editable,
  handleEditTemporalInput,
  handleInput,
  repMaximums,
  addTempSet,
  removeSet,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  units,
  addingSet,
  onAddSet,
  onSubmitSet,
  onDiscardSet,
}) => {
  const isAssistant = exercise.type === "assistant";
  const hasFreeForm = exercise.sets.some((set) => set.freeform);
  const isTemporalSet = (idx) => {
    let temporalRow = false;

    if (!newExercise) return false;
    if (newExercise.length) {
      const result = newExercise.find(
        (_ex) => parseInt(_ex.pos) === parseInt(idx)
      );
      if (result) temporalRow = result;
    } else {
      if (parseInt(newExercise.pos) === parseInt(idx)) {
        temporalRow = newExercise;
      }
    }

    return editDayMode && editable && newExercise && temporalRow;
  };

  const isExerciseStarter =
    isAssistant && exercise.primary === "assistant" && !exercise.parameters;
  return isAssistant && hasFreeForm ? (
    editDayMode && !editable ? (
      <TooltipPortal
        childrenContainerClass={"table-editor-with-tooltip"}
        body="This exercise has already been completed, cannot edit"
      >
        <Assistant
          finalSets={finalSets}
          editDayMode={editDayMode}
          editable={editable}
          exerciseId={exercise._id}
          handleInput={handleInput}
          addTempSet={addTempSet}
          removeSet={removeSet}
        />
      </TooltipPortal>
    ) : (
      <Assistant
        finalSets={finalSets}
        editDayMode={editDayMode}
        editable={editable}
        exerciseId={exercise._id}
        handleInput={handleInput}
        addTempSet={addTempSet}
        removeSet={removeSet}
        addingSet={addingSet}
        onAddSet={onAddSet}
        onSubmitSet={onSubmitSet}
        onDiscardSet={onDiscardSet}
      />
    )
  ) : editDayMode && !editable ? (
    <TooltipPortal
      childrenContainerClass={"table-editor-with-tooltip"}
      body="This exercise has already been completed, cannot edit"
    >
      <Table
        {...{
          editDayMode,
          editable,
          isAssistant,
          isExerciseStarter,
          exercise,
          finalSets,
          someHasRPE,
          intensityMeasure,
          handleEditTemporalInput,
          handleInput,
          isTemporalSet,
          repMaximums,
          addTempSet,
          removeSet,
          withWeight,
          fromMetric,
          isRiR,
          units,
        }}
      />
    </TooltipPortal>
  ) : (
    <Table
      {...{
        editDayMode,
        editable,
        isAssistant,
        isExerciseStarter,
        exercise,
        finalSets,
        someHasRPE,
        intensityMeasure,
        handleEditTemporalInput,
        handleInput,
        isTemporalSet,
        repMaximums,
        addTempSet,
        removeSet,
        withWeight,
        fromMetric,
        isRiR,
        units,
        addingSet,
        onAddSet,
        onSubmitSet,
        onDiscardSet,
      }}
    />
  );
};

const Table = ({
  editDayMode,
  editable,
  isAssistant,
  isExerciseStarter,
  exercise,
  finalSets,
  someHasRPE,
  intensityMeasure,
  handleEditTemporalInput,
  handleInput,
  isTemporalSet,
  repMaximums,
  addTempSet,
  removeSet,
  withWeight,
  fromMetric,
  isRiR,
  units,
  addingSet,
  onAddSet,
  onSubmitSet,
  onDiscardSet,
}) => {
  return (
    <table
      className={
        editDayMode && editable
          ? "tr-table-editing tr-table"
          : editDayMode
          ? "tr-table not-editable"
          : "tr-table"
      }
    >
      <thead>
        {!isAssistant || isExerciseStarter ? (
          <tr>
            <th>Sets</th>
            <th>Reps</th>
            <th>{isRiR ? "RiR" : "RPE"}</th>
            <th>Load</th>
            {isExerciseStarter ? null : <th>%1RM</th>}
            {exercise.sets.length && editDayMode && editable ? (
              <th className="invisible">-</th>
            ) : null}
          </tr>
        ) : (
          <tr>
            {exercise &&
              exercise.parameters &&
              exercise.parameters.map((param = "", idx) => (
                <th key={idx}>
                  {[RPE, RPEAMRAP].includes(param) ? intensityMeasure : param}
                </th>
              ))}
            {exercise.sets.length && editDayMode && editable ? (
              <th className="invisible">-</th>
            ) : null}
          </tr>
        )}
      </thead>
      <tbody>
        {!isAssistant || isExerciseStarter
          ? finalSets &&
            finalSets.map((set, idx) => {
              const isTemporal = isTemporalSet(idx);
              const paramsRow = {
                exerciseId: exercise._id,
                exerciseHasCustomRPE:
                  exercise.type === "secondary" && exercise.secondaryRepMaximum
                    ? exercise.secondaryRepMaximum
                    : false,
                primary: exercise.primary,
                set: isTemporal ? temporalRow : set,
                handler: isTemporal ? handleEditTemporalInput : handleInput,
                idx: `${exercise._id}-${isTemporal ? "-new" : idx}`,
                someHasRPE,
                hasSets: exercise.sets.length,
                repMaximums,
                addTempSet,
                removeSet,
                editDayMode,
                editable,
                withWeight,
                fromMetric,
                isExerciseStarter,
                intensityMeasure,
                isRiR,
                units,
                hasLoadDrop: exercise?.sets?.[idx]?.loadDrop,
              };

              return (
                <TableRow
                  key={`${exercise._id}-${idx}`}
                  {...paramsRow}
                  addingSet={addingSet}
                  onAddSet={onAddSet}
                  onSubmitSet={onSubmitSet}
                  onDiscardSet={onDiscardSet}
                />
              );
            })
          : finalSets &&
            finalSets.map((set, idx) => {
              const isTemporal = isTemporalSet(idx);
              const paramsRow = {
                idx,
                exercise,
                handler: isTemporal ? handleEditTemporalInput : handleInput,
                editDayMode,
                editable,
                set,
                isTemporal,
                addTempSet,
                removeSet,
                withWeight,
                fromMetric,
                intensityMeasure,
                isRiR,
                units,
                hasLoadDrop: exercise?.sets?.[idx]?.loadDrop,
              };
              return (
                <TableForAssistant
                  key={`${exercise._id}-${idx}`}
                  {...paramsRow}
                />
              );
            })}
      </tbody>
    </table>
  );
};

export default ExerciseContent;
